import { useEffect, useState } from 'react';
import { useAimdoc } from '@aimdoc/sdk-react';
import type { AimdocServicesClient } from '@aimdoc/sdk';
import useApi from '../api';
import { useUser } from './UserContext';

// Dogfooded Services SDK client for the dashboard: the same public client a
// customer configures in their product, pointed at the Aimdoc org's
// publishable key with one-time identity assertions minted by
// /users/me/aimdoc-services-identity (no secret reaches the browser). The
// client is configured once per signed-in user and shared — configure()
// resets the session, so callers must never reconfigure it themselves. It is
// keyed by user id: signing out and back in as someone else (no reload)
// reconfigures, so the second user never rides the first user's session.
let configuredForUserId: string | null = null;
let configuring: { userId: string; promise: Promise<boolean> } | null = null;

/**
 * The shared, identified Services client for the signed-in dashboard user,
 * or null while it is being set up / when dogfooding is not configured
 * (no AIMDOC_SERVICES_PUBLISHABLE_KEY on the server).
 */
export function useAimdocServicesClient(): AimdocServicesClient | null {
  const aimdoc = useAimdoc();
  const { get } = useApi();
  const { user } = useUser();
  const userId = user?.id ? String(user.id) : null;
  const [readyForUserId, setReadyForUserId] = useState<string | null>(configuredForUserId);

  useEffect(() => {
    if (!userId) return;
    if (configuredForUserId === userId) {
      setReadyForUserId(userId);
      return;
    }
    let cancelled = false;
    if (!configuring || configuring.userId !== userId) {
      const entry: { userId: string; promise: Promise<boolean> } = { userId, promise: Promise.resolve(false) };
      entry.promise = (async () => {
        try {
          const response = await get('/users/me/aimdoc-services-identity');
          const key = (response.data?.publishable_key as string | null) || null;
          if (!key) return false;
          aimdoc.configureServices({
            publishableKey: key,
            apiUrl: (import.meta.env.VITE_API_URL as string) || undefined,
            // Fresh assertion per session exchange: each carries a one-time jti.
            getIdentityAssertion: async () => {
              const fresh = await get('/users/me/aimdoc-services-identity');
              return (fresh.data?.assertion as string | null) || undefined;
            },
          });
          configuredForUserId = userId;
          return true;
        } catch (error) {
          console.error('Aimdoc services client setup failed:', error);
          return false;
        } finally {
          if (configuring === entry) configuring = null;
        }
      })();
      configuring = entry;
    }
    void configuring.promise.then((ok) => {
      if (!cancelled && ok) setReadyForUserId(userId);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, get, aimdoc]);

  return readyForUserId !== null && readyForUserId === userId ? aimdoc.services : null;
}
